import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Landing from "./pages/Landing";
import Register from "./pages/Register";
import Onboarding, { OnboardingDone } from "./pages/Onboarding";
import Login from "./pages/Login";
import VerifyLink from "./pages/VerifyLink";
import NotFound from "./pages/NotFound";
import AppDashboard from "./pages/app/AppDashboard";
import AppCalls from "./pages/app/AppCalls";
import AppCallDetail from "./pages/app/AppCallDetail";
import AppChapter from "./pages/app/AppChapter";
import AppPhotos from "./pages/app/AppPhotos";
import AppFamily from "./pages/app/AppFamily";
import AppStoryteller from "./pages/app/AppStoryteller";
import AppAccount from "./pages/app/AppAccount";
import AppInvoices from "./pages/app/AppInvoices";
import AppBook from "./pages/app/AppBook";
import AppBookShare from "./pages/app/AppBookShare";
import AppBookText from "./pages/app/AppBookText";
import AppPostalCard from "./pages/app/AppPostalCard";
import AppBuchhaltung from "./pages/app/AppBuchhaltung";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminVerifyLink from "./pages/admin/AdminVerifyLink";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminBookText from "./pages/admin/AdminBookText";
import AdminChapterDetail from "./pages/admin/AdminChapterDetail";
import AdminCodes from "./pages/admin/AdminCodes";
import AdminPreview from "./pages/admin/AdminPreview";
import AdminUserNew from "./pages/admin/AdminUserNew";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import Datenschutz from "./pages/legal/Datenschutz";
import AGB from "./pages/legal/AGB";
import Impressum from "./pages/legal/Impressum";
import Pakete from "./pages/marketing/Pakete";
import FAQ from "./pages/marketing/FAQ";
import UeberUns from "./pages/marketing/UeberUns";
import BiografieSchreibenLassen from "./pages/marketing/BiografieSchreibenLassen";
import WieFunktioniertVitanara from "./pages/marketing/WieFunktioniertVitanara";
import Preise from "./pages/marketing/Preise";
import GeschenkOma from "./pages/marketing/GeschenkOma";
import GeschenkOpa from "./pages/marketing/GeschenkOpa";
import GoldeneHochzeit from "./pages/marketing/GoldeneHochzeit";
import LebensgeschichteHoerbuch from "./pages/marketing/LebensgeschichteHoerbuch";
import LebensgeschichtePodcast from "./pages/marketing/LebensgeschichtePodcast";
import LoginVerify from "./pages/LoginVerify";

// Preserve query string when redirecting /dashboard → /app.
function DashboardRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/app${search}`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/onboarding/done" element={<OnboardingDone />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/login/verify" element={<LoginVerify />} />
      <Route path="/auth/verify" element={<VerifyLink />} />
      <Route path="/pakete" element={<Pakete />} />
      <Route
        path="/biografie-schreiben-lassen"
        element={<BiografieSchreibenLassen />}
      />
      <Route
        path="/wie-funktioniert-vitanara"
        element={<WieFunktioniertVitanara />}
      />
      <Route path="/preise" element={<Preise />} />
      <Route path="/geschenk-fuer-oma" element={<GeschenkOma />} />
      <Route path="/geschenk-fuer-opa" element={<GeschenkOpa />} />
      <Route
        path="/goldene-hochzeit-geschenk"
        element={<GoldeneHochzeit />}
      />
      <Route
        path="/lebensgeschichte-als-hoerbuch"
        element={<LebensgeschichteHoerbuch />}
      />
      <Route
        path="/lebensgeschichte-als-podcast"
        element={<LebensgeschichtePodcast />}
      />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/ueber-uns" element={<UeberUns />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/impressum" element={<Impressum />} />

      {/* Authenticated v2 /app/* surface */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/calls"
        element={
          <ProtectedRoute>
            <AppCalls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/calls/:id"
        element={
          <ProtectedRoute>
            <AppCallDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/chapter/:n"
        element={
          <ProtectedRoute>
            <AppChapter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/photos"
        element={
          <ProtectedRoute>
            <AppPhotos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/family"
        element={
          <ProtectedRoute>
            <AppFamily />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/storyteller"
        element={
          <ProtectedRoute>
            <AppStoryteller />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/account"
        element={
          <ProtectedRoute>
            <AppAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/invoices"
        element={
          <ProtectedRoute>
            <AppInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/book"
        element={
          <ProtectedRoute>
            <AppBook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/book/text"
        element={
          <ProtectedRoute>
            <AppBookText />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/book/share"
        element={
          <ProtectedRoute>
            <AppBookShare />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/postal-card"
        element={
          <ProtectedRoute>
            <AppPostalCard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/buchhaltung"
        element={
          <ProtectedRoute>
            <AppBuchhaltung />
          </ProtectedRoute>
        }
      />

      {/* Legacy authenticated routes — redirect into the new /app surface.
          Old page components (Dashboard, NarratorDetail, ChapterDetail,
          Settings) are intentionally kept on disk for rollback. */}
      <Route path="/dashboard" element={<DashboardRedirect />} />
      <Route
        path="/narrators/:phone"
        element={<Navigate to="/app/storyteller" replace />}
      />
      <Route
        path="/narrators/:phone/chapters/:title"
        element={<Navigate to="/app" replace />}
      />
      <Route
        path="/narrators/:phone/settings"
        element={<Navigate to="/app/storyteller" replace />}
      />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth/verify" element={<AdminVerifyLink />} />
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/admins"
        element={
          <AdminProtectedRoute>
            <AdminAdmins />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/codes"
        element={
          <AdminProtectedRoute>
            <AdminCodes />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:phone/preview"
        element={
          <AdminProtectedRoute>
            <AdminPreview />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:phone/chapters/:title"
        element={
          <AdminProtectedRoute>
            <AdminChapterDetail />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:phone/book-text"
        element={
          <AdminProtectedRoute>
            <AdminBookText />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <AdminProtectedRoute>
            <AdminUserNew />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:phone"
        element={
          <AdminProtectedRoute>
            <AdminUserDetail />
          </AdminProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
